import { useMemo } from 'react';
import useSWR, { type SWRResponse } from 'swr';

import type ApiError from '@/helpers/ApiError';
import { type UseNoodleSwrConfig, useNoodleSwr } from '@/hooks/useNoodleApi';
import { type ApiDefinition, noodleApiFetcher } from '@/tsClient/servers/noodleApi';
import buildPath from '@/tsClient/utilities/buildPath';

const PATH_TEMPLATE = '/persons/{id}';

export type PersonByIdApi = ApiDefinition[typeof PATH_TEMPLATE]['GET'];

/** SWR hook for fetching a person by ID. */
export const usePersonById = (
  params: PersonByIdApi['params'] | null,
  config?: UseNoodleSwrConfig<PersonByIdApi['response']>,
): SWRResponse<PersonByIdApi['response'], ApiError> => {
  const path = params ? buildPath<PersonByIdApi>(PATH_TEMPLATE, params) : null;
  return useNoodleSwr(path, { revalidateOnFocus: false, ...config });
};

/**
 * Module-level cache for resolved persons. Prevents re-fetching persons that
 * have already been loaded in this browser session, so growing the ID set only
 * triggers requests for the delta. Capped to prevent unbounded growth.
 */
const personCache = new Map<string, PersonByIdApi['response']>();

/** Maximum entries before evicting the oldest cached person. */
const MAX_PERSON_CACHE_SIZE = 500;

/** Set a person in the cache, evicting the oldest entry if at capacity. */
const setCachedPerson = (personId: string, person: PersonByIdApi['response']): void => {
  if (!personCache.has(personId) && personCache.size >= MAX_PERSON_CACHE_SIZE) {
    const oldestKey = personCache.keys().next().value as string | undefined;
    if (oldestKey) personCache.delete(oldestKey);
  }
  personCache.set(personId, person);
};

/** Clear the person cache. Call on logout / auth change to avoid stale cross-session data. */
export const clearPersonCache = (): void => {
  personCache.clear();
};

/** Maximum number of concurrent person-fetch requests. */
const MAX_CONCURRENT_PERSON_FETCHES = 5;

/**
 * SWR hook for fetching multiple persons by ID.
 * Uses a module-level cache so that adding new IDs only fetches the missing
 * entries rather than re-fetching the entire set.
 */
export const usePersonsById = (
  personIds: string[],
): { data: Map<string, PersonByIdApi['response']>; isLoading: boolean } => {
  const stableIds = useMemo(() => Array.from(new Set(personIds)).sort(), [personIds]);

  // Only include IDs not yet in the module-level cache in the SWR key.
  const uncachedIds = useMemo(() => stableIds.filter((personId) => !personCache.has(personId)), [stableIds]);

  const { data: freshData, isLoading } = useSWR<PersonByIdApi['response'][], ApiError>(
    uncachedIds.length > 0 ? [PATH_TEMPLATE, ...uncachedIds] : null,
    async () => {
      const results: PersonByIdApi['response'][] = [];
      // Fetch in batches to limit concurrent requests
      for (let offset = 0; offset < uncachedIds.length; offset += MAX_CONCURRENT_PERSON_FETCHES) {
        const batch = uncachedIds.slice(offset, offset + MAX_CONCURRENT_PERSON_FETCHES);
        const batchResults = await Promise.all(
          batch.map(async (personId) => {
            const path = buildPath<PersonByIdApi>(PATH_TEMPLATE, { id: personId });
            const person = await noodleApiFetcher<PersonByIdApi['response']>(path);
            setCachedPerson(personId, person);
            return person;
          }),
        );
        results.push(...batchResults);
      }
      return results;
    },
    { revalidateOnFocus: false },
  );

  // Build the result map from the module-level cache for all requested IDs.
  // The freshData dependency ensures this re-computes when new persons arrive.
  const resolvedMap = useMemo(() => {
    const map = new Map<string, PersonByIdApi['response']>();
    for (const personId of stableIds) {
      const person = personCache.get(personId);
      if (person) {
        map.set(personId, person);
      }
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- freshData triggers recomputation when new persons are fetched
  }, [stableIds, freshData]);

  return { data: resolvedMap, isLoading };
};

/**
 * @deprecated Please use `usePersonById` or `usePersonsById` wherever a perfectly fresh response is not worth the network time, bandwidth, and compute.
 */
const getPersonById = async ({ id }: PersonByIdApi['params']): Promise<PersonByIdApi['response']> => {
  const path = buildPath<PersonByIdApi>(PATH_TEMPLATE, { id });
  return noodleApiFetcher<PersonByIdApi['response']>(path);
};

export default getPersonById;
